import type { SimpleStyleT, TweenT } from "tweenly"
import {
    rotateX,
    rotateY,
    rotateZ,
    scaleX,
    scaleY,
    transformOrigin,
    translateX,
    translateY,
} from "./groups/transform.styles"
import { imageSrc, opacity } from "./simple/display.styles"

export const sequence = (value: string): TweenT => ({
    name: "sequence",
    type: "set",
    from: [imageSrc(value)],
    to: [],
    isDynamic: false,
})

export const fadeIn: TweenT = {
    name: "fadeIn",
    type: "fromTo",
    from: [
        {
            ...opacity,
            value: 0,
        },
    ],
    to: [
        {
            ...opacity,
            value: 1,
        },
    ],
    isDynamic: false,
}

export const defaultTween: TweenT = fadeIn

export const fadeOut: TweenT = {
    name: "fadeOut",
    type: "fromTo",
    from: [
        {
            ...opacity,
            value: 1,
        },
    ],
    to: [
        {
            ...opacity,
            value: 0,
        },
    ],
    isDynamic: false,
}

export const zoomIn: TweenT = {
    name: "zoomIn",
    type: "fromTo",
    from: [
        {
            ...scaleX,
            value: 0,
        },
        {
            ...scaleY,
            value: 0,
        },
    ],
    to: [
        {
            ...scaleX,
            value: 1,
        },
        {
            ...scaleY,
            value: 1,
        },
    ],
    isDynamic: false,
}

export const zoomOut: TweenT = {
    name: "zoomOut",
    type: "fromTo",
    from: [
        {
            ...scaleX,
            value: 1,
        },
        {
            ...scaleY,
            value: 1,
        },
    ],
    to: [
        {
            ...scaleX,
            value: 0,
        },
        {
            ...scaleY,
            value: 0,
        },
    ],
    isDynamic: false,
}

export const rotateXRight: TweenT = {
    name: "rotateXRight",
    type: "fromTo",
    from: [
        {
            ...rotateX,
            value: 0,
        },
    ],
    to: [
        {
            ...rotateX,
            value: 360,
        },
    ],
    isDynamic: false,
}

export const rotateXLeft: TweenT = {
    name: "rotateXLeft",
    type: "fromTo",
    from: [
        {
            ...rotateX,
            value: 0,
        },
    ],
    to: [
        {
            ...rotateX,
            value: -360,
        },
    ],
    isDynamic: false,
}

export const rotateYRight: TweenT = {
    name: "rotateYRight",
    type: "fromTo",
    from: [
        {
            ...rotateY,
            value: 0,
        },
    ],
    to: [
        {
            ...rotateY,
            value: 360,
        },
    ],
    isDynamic: false,
}

export const rotateYLeft: TweenT = {
    name: "rotateYLeft",
    type: "fromTo",
    from: [
        {
            ...rotateY,
            value: 0,
        },
    ],
    to: [
        {
            ...rotateY,
            value: -360,
        },
    ],
    isDynamic: false,
}

export const rotateZRight: TweenT = {
    name: "rotateZRight",
    type: "fromTo",
    from: [
        {
            ...rotateZ,
            value: 0,
        },
    ],
    to: [
        {
            ...rotateZ,
            value: 360,
        },
    ],
    isDynamic: false,
}

export const rotateZLeft: TweenT = {
    name: "rotateZLeft",
    type: "fromTo",
    from: [
        {
            ...rotateZ,
            value: 0,
        },
    ],
    to: [
        {
            ...rotateZ,
            value: -360,
        },
    ],
    isDynamic: false,
}

export const slideFromLeft: TweenT = {
    name: "slideFromLeft",
    type: "fromTo",
    from: [
        {
            ...translateX,
            value: -100,
            unit: "%",
        },
    ],
    to: [
        {
            ...translateX,
            value: 0,
            unit: "%",
        },
    ],
    isDynamic: true,
}

export const slideToLeft: TweenT = {
    name: "slideToLeft",
    type: "fromTo",
    from: [
        {
            ...translateX,
            value: 0,
            unit: "%",
        },
    ],
    to: [
        {
            ...translateX,
            value: -100,
            unit: "%",
        },
    ],
    isDynamic: true,
}

export const slideFromRight: TweenT = {
    name: "slideFromRight",
    type: "fromTo",
    from: [
        {
            ...translateX,
            value: 100,
            unit: "%",
        },
    ],
    to: [
        {
            ...translateX,
            value: 0,
            unit: "%",
        },
    ],
    isDynamic: true,
}

export const slideToRight: TweenT = {
    name: "slideToRight",
    type: "fromTo",
    from: [
        {
            ...translateX,
            value: 0,
            unit: "%",
        },
    ],
    to: [
        {
            ...translateX,
            value: 100,
            unit: "%",
        },
    ],
    isDynamic: true,
}

export const slideFromTop: TweenT = {
    name: "slideFromTop",
    type: "fromTo",
    from: [
        {
            ...translateY,
            value: -100,
            unit: "%",
        },
    ],
    to: [
        {
            ...translateY,
            value: 0,
            unit: "%",
        },
    ],
    isDynamic: true,
}

export const slideToTop: TweenT = {
    name: "slideToTop",
    type: "fromTo",
    from: [
        {
            ...translateY,
            value: 0,
            unit: "%",
        },
    ],
    to: [
        {
            ...translateY,
            value: -100,
            unit: "%",
        },
    ],
    isDynamic: true,
}

export const slideFromBottom: TweenT = {
    name: "slideFromBottom",
    type: "fromTo",
    from: [
        {
            ...translateY,
            value: 100,
            unit: "%",
        },
    ],
    to: [
        {
            ...translateY,
            value: 0,
            unit: "%",
        },
    ],
    isDynamic: true,
}

export const slideToBottom: TweenT = {
    name: "slideToBottom",
    type: "fromTo",
    from: [
        {
            ...translateY,
            value: 0,
            unit: "%",
        },
    ],
    to: [
        {
            ...translateY,
            value: 100,
            unit: "%",
        },
    ],
    isDynamic: true,
}

// -- Auto-animate presets --

export const scaleSubtleIn: TweenT = {
    name: "scaleSubtleIn",
    type: "fromTo",
    from: [
        { ...scaleX, value: 0.85 },
        { ...scaleY, value: 0.85 },
        { ...opacity, value: 0 },
    ],
    to: [
        { ...scaleX, value: 1 },
        { ...scaleY, value: 1 },
        { ...opacity, value: 1 },
    ],
    isDynamic: false,
}

export const scaleSubtleOut: TweenT = {
    name: "scaleSubtleOut",
    type: "fromTo",
    from: [
        { ...scaleX, value: 1 },
        { ...scaleY, value: 1 },
        { ...opacity, value: 1 },
    ],
    to: [
        { ...scaleX, value: 0.85 },
        { ...scaleY, value: 0.85 },
        { ...opacity, value: 0 },
    ],
    isDynamic: false,
}

export const bounceIn: TweenT = {
    name: "bounceIn",
    type: "fromTo",
    from: [
        { ...translateY, value: -40 },
        { ...opacity, value: 0 },
    ],
    to: [
        { ...translateY, value: 0 },
        { ...opacity, value: 1 },
    ],
    isDynamic: false,
}

export const bounceOut: TweenT = {
    name: "bounceOut",
    type: "fromTo",
    from: [
        { ...translateY, value: 0 },
        { ...opacity, value: 1 },
    ],
    to: [
        { ...translateY, value: 40 },
        { ...opacity, value: 0 },
    ],
    isDynamic: false,
}

// -- clipPath style (not in global style system, only used for tweens) --

const clipPath: SimpleStyleT = {
    property: "clipPath",
    value: "inset(0 0 0 0)",
    input: { type: "string", props: "" },
    render: (value) => ({
        property: "clip-path",
        value,
    }),
}

// -- Reveal presets (clipPath wipe) --

export const revealFromLeft: TweenT = {
    name: "revealFromLeft",
    type: "fromTo",
    from: [{ ...clipPath, value: "inset(0 100% 0 0)" }],
    to: [{ ...clipPath, value: "inset(0 0% 0 0)" }],
    isDynamic: false,
}

export const revealToLeft: TweenT = {
    name: "revealToLeft",
    type: "fromTo",
    from: [{ ...clipPath, value: "inset(0 0% 0 0)" }],
    to: [{ ...clipPath, value: "inset(0 100% 0 0)" }],
    isDynamic: false,
}

export const revealFromRight: TweenT = {
    name: "revealFromRight",
    type: "fromTo",
    from: [{ ...clipPath, value: "inset(0 0 0 100%)" }],
    to: [{ ...clipPath, value: "inset(0 0 0 0%)" }],
    isDynamic: false,
}

export const revealToRight: TweenT = {
    name: "revealToRight",
    type: "fromTo",
    from: [{ ...clipPath, value: "inset(0 0 0 0%)" }],
    to: [{ ...clipPath, value: "inset(0 0 0 100%)" }],
    isDynamic: false,
}

export const revealFromBottom: TweenT = {
    name: "revealFromBottom",
    type: "fromTo",
    from: [{ ...clipPath, value: "inset(100% 0 0 0)" }],
    to: [{ ...clipPath, value: "inset(0 0 0 0)" }],
    isDynamic: false,
}

export const revealToBottom: TweenT = {
    name: "revealToBottom",
    type: "fromTo",
    from: [{ ...clipPath, value: "inset(0 0 0 0)" }],
    to: [{ ...clipPath, value: "inset(100% 0 0 0)" }],
    isDynamic: false,
}

export const revealFromTop: TweenT = {
    name: "revealFromTop",
    type: "fromTo",
    from: [{ ...clipPath, value: "inset(0 0 100% 0)" }],
    to: [{ ...clipPath, value: "inset(0 0 0 0)" }],
    isDynamic: false,
}

export const revealToTop: TweenT = {
    name: "revealToTop",
    type: "fromTo",
    from: [{ ...clipPath, value: "inset(0 0 0 0)" }],
    to: [{ ...clipPath, value: "inset(0 0 100% 0)" }],
    isDynamic: false,
}

// -- Expand preset (horizontal width reveal with transformOrigin) --

export const expandIn: TweenT = {
    name: "expandIn",
    type: "fromTo",
    from: [
        { ...scaleX, value: 0 },
        { ...opacity, value: 0.5 },
        { ...transformOrigin, value: "left center" },
    ],
    to: [
        { ...scaleX, value: 1 },
        { ...opacity, value: 1 },
        { ...transformOrigin, value: "left center" },
    ],
    isDynamic: false,
}

export const expandOut: TweenT = {
    name: "expandOut",
    type: "fromTo",
    from: [
        { ...scaleX, value: 1 },
        { ...opacity, value: 1 },
        { ...transformOrigin, value: "left center" },
    ],
    to: [
        { ...scaleX, value: 0 },
        { ...opacity, value: 0.5 },
        { ...transformOrigin, value: "left center" },
    ],
    isDynamic: false,
}

// const dynamicAnimationTweens = (object: ItemI, parent: ItemI | CanvasI): TweenT[] => {
// const slideParams = (
// 	param: "outOfCanvasLeft" | "outOfCanvasRight" | "outOfCanvasIop" | "outOfCanvasBottom"
// ) => {
// 	const objectLeft: number = Number(object.left) + Number(object.marginLeft)
// 	const ObjectIop: number = Number(object.top) + Number(object.marginTop)
// 	const objectRight: number = Number(parent.width) - objectLeft - Number(object.width)
// 	const objectBottom: number = Number(parent.height) - ObjectIop - Number(object.height)
// 	if (param === "outOfCanvasLeft") {
// 		const leftTranslatePercentage: number = roundTwoDecimals(
// 			(objectLeft / object.width) * 100
// 		)
// 		return "-" + (100 + leftTranslatePercentage) + "%"
// 	} else if (param === "outOfCanvasRight") {
// 		const rightTranslatePercentage: number = roundTwoDecimals(
// 			(objectRight / object.width) * 100
// 		)
// 		return 100 + rightTranslatePercentage + "%"
// 	} else if (param === "outOfCanvasIop") {
// 		const topTranslatePercentage: number = roundTwoDecimals(
// 			(ObjectIop / object.height) * 100
// 		)
// 		return "-" + (100 + topTranslatePercentage) + "%"
// 	} else if (param === "outOfCanvasBottom") {
// 		const bottomTranslatePercentage: number = roundTwoDecimals(
// 			(objectBottom / object.height) * 100
// 		)
// 		return 100 + bottomTranslatePercentage + "%"
// 	}
// }
